import React, { useState, useEffect, useMemo, Suspense } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Box,
  Checkbox,
  Button,
  FormControlLabel,
  TableCell,
  TableRow,
  TableHead,
  Table,
  TableBody,
  TableSortLabel,
  Tooltip,
  MenuItem,
  Select,
  SelectChangeEvent,
  Menu,
  Paper,
  Chip,
  Divider,
  alpha,
  Badge,
  Stack,
  InputBase,
  TextField,
  InputAdornment,
  Toolbar,
  useTheme,
  Popper,
  Fade,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useAppDispatch, useAppSelector } from 'src/store';
import { deleteContact, selectContactById, updateContact } from 'src/features/account/contacts.slice';
import { selectAllFilteredContacts, fetchFilteredContacts, selectIsFilterActive } from 'src/features/account/filteredContacts.slice';
import { Contact } from 'src/types/contacts';
import { styled } from '@mui/material/styles';
// Import the conversation dialog
import {
  Star,
  MailOutline,
  Phone,
  Delete,
  Close as CloseIcon,
  ChatBubbleOutline,
  Message,
  Person,
  ImportExport,
  Label,
  Psychology,
} from '@mui/icons-material';
// Import the conversation dialog
import ConversationCreateDialogV2 from '../Channels/ConversationCreateDialogV2';
import { GenerateActivityButton } from './index';
import ContactEmailButton from './ContactEmailButton';

// Define sorting types
type SortDirection = 'asc' | 'desc';
type SortField = 'created_at' | 'updated_at' | 'has_conversations' | 'name' | 'score';

interface ContactsListProps {
  contacts?: Contact[];
  onSelectionChange: (selectedIds: string[]) => void;
  selectedContactIds: string[];
  onOpenContactDialog?: (contactId: string) => void;
}

interface ContactListItemProps {
  contact: Contact;
  isSelected: boolean;
  handleToggle: (contactId: string) => void;
  setEditDialogOpen: (open: boolean) => void;
  setSelectedContactId: (contactId: string | null) => void;
  onOpenContact?: () => void;
}

// Table column width definitions (to maintain consistent column sizes)
const ColumnWidths = {
  checkbox: '28px',  // Further reduced from 30px
  favorite: '28px',  // Further reduced from 30px
  info: '480px',     // Increased from 450px to accommodate shared notes
  date: '120px',
  score: '120px',
  conversations: '120px',
  actions: '120px', // Aligned right
};

// Styling for table
const StyledTable = styled(Table)(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderCollapse: 'collapse',
  '& .MuiTableCell-root': {
    padding: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderBottom: 'none',
    '&.MuiTableCell-head': {
      fontWeight: 500,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : theme.palette.background.default
    }
  },
  '& .MuiTableHead-root': {
    position: 'sticky',
    top: 0,
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : theme.palette.background.default,
    zIndex: 2
  },
  '& .MuiTableBody-root': {
    position: 'relative'
  },
  '& .MuiTableRow-root': {
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.action.hover, 0.1)
        : alpha(theme.palette.action.hover, 0.05)
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.15)
        : alpha(theme.palette.primary.main, 0.05),
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? alpha(theme.palette.primary.main, 0.2)
          : alpha(theme.palette.primary.main, 0.08)
      }
    }
  }
}));

// Styled table rows
const StyledTableRow = styled(TableRow)<{
  selected?: boolean;
  hover?: boolean;
  onClick?: (event: React.MouseEvent<HTMLTableRowElement>) => void;
}>(({ theme }) => ({
  cursor: 'pointer',
  height: '64px', // Increased row height
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.action.hover, 0.1)
      : alpha(theme.palette.action.hover, 0.05),
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.15)
      : alpha(theme.palette.primary.main, 0.05),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.2)
        : alpha(theme.palette.primary.main, 0.08),
    }
  }
}));

// Table wrapper
const TableWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark'
    ? theme.palette.background.paper
    : theme.palette.background.default,
  borderRadius: theme.shape.borderRadius,
  border: 'none',
}));

// Status dot for contacts
const StatusDot = styled('span')<{ status: string }>(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'active': return theme.palette.success.main;
      case 'pending': return theme.palette.warning.main;
      case 'inactive': return theme.palette.grey[400];
      default: return theme.palette.info.main; // new
    }
  };

  return {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: getStatusColor(),
    display: 'inline-block',
    marginRight: 8,
  };
});

// Animated delete action container
const AnimatedDeleteAction = styled('div')({
  position: 'relative',
  display: 'inline-flex',
  width: '32px', // Fixed width to prevent layout shift when confirming delete
});

// Action buttons container
const ActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(0.5),
}));

const ContactListItem: React.FC<ContactListItemProps> = ({
  contact,
  isSelected,
  handleToggle,
  setEditDialogOpen,
  setSelectedContactId,
  onOpenContact,
}) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [isConfirming, setIsConfirming] = useState(false);
  const [isFavoriting, setIsFavoriting] = useState(false);
  const [conversationDialogOpen, setConversationDialogOpen] = useState(false);
  const [showNotesPreview, setShowNotesPreview] = useState(false);
  const [notesAnchorEl, setNotesAnchorEl] = useState<null | HTMLElement>(null);

  const contactName = contact.name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.phone_number || contact.email || '';

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedContactId(contact.id);
    setEditDialogOpen(true);
  };

  const handleRowClick = () => {
    if (onOpenContact) {
      onOpenContact();
    } else {
      setSelectedContactId(contact.id);
      setEditDialogOpen(true);
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsConfirming(true);
  };

  const handleConfirmDelete = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(deleteContact(contact.id)).then(() => {
      dispatch(fetchFilteredContacts({ query: '' }));
    });
  };

  const handleFavoriteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (contact) {
      const updatedTags = contact.tags?.includes('favorite')
        ? contact.tags.filter(tag => tag !== 'favorite')
        : [...(contact.tags || []), 'favorite'];
      setIsFavoriting(true);
      dispatch(updateContact({
        contactId: contact.id.toString(),
        updates: {
          favorite: !contact.favorite,
          tags: updatedTags
        }
      })).then(() => {
        setIsFavoriting(false);
      });
    }
  };

  const handleOpenConversation = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // Make sure the contact object is valid before opening the dialog
    if (contact && contact.id) {
      setConversationDialogOpen(true);
    } else {
      console.error('Cannot open conversation: Invalid contact object');
    }
  };

  // Get contact status - this could be based on tags, activity, etc.
  const getContactStatus = (): string => {
    if (contact.last_contacted) {
      const lastActive = new Date(contact.last_contacted);
      const now = new Date();
      const daysSinceActive = Math.floor((now.getTime() - lastActive.getTime()) / (1000 * 60 * 60 * 24));

      if (daysSinceActive < 7) return 'active';
      if (daysSinceActive < 30) return 'pending';
      return 'inactive';
    }
    return 'new';
  };

  // Get tooltip text for status dot
  const getStatusTooltip = (status: string): string => {
    switch (status) {
      case 'active':
        return 'Active: Contacted within the last 7 days';
      case 'pending':
        return 'Pending: Contacted between 7-30 days ago';
      case 'inactive':
        return 'Inactive: No contact in over 30 days';
      case 'new':
        return 'New: Never contacted';
      default:
        return 'Unknown status';
    }
  };

  // Update the formatDate function in ContactListItem to return both formatted date and full datetime
  const formatDate = (dateString?: Date) => {
    if (!dateString) return { display: 'N/A', full: 'N/A' };
    const date = new Date(dateString);

    // Format full datetime for tooltip
    const fullDateTime = date.toLocaleString([], {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    // If it's today, show the time, otherwise show the date
    const today = new Date();
    const isToday = date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    let display;
    if (isToday) {
      display = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (date.getFullYear() === today.getFullYear()) {
      // If it's this year, don't show the year
      display = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    } else {
      display = date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
    }

    return { display, full: fullDateTime };
  };

  return (
    <StyledTableRow
      selected={isSelected}
      onClick={handleRowClick}
      hover
    >
      <TableCell padding="checkbox" sx={{
        width: ColumnWidths.checkbox,
        pr: 0, // No right padding
      }}>
        <Checkbox
          color="primary"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            handleToggle(contact.id);
          }}
          onClick={(e) => e.stopPropagation()}
          disabled={contact.deleting}
          size="small"
          sx={{ p: 1 }} // Smaller padding for the checkbox itself
        />
      </TableCell>

      <TableCell padding="checkbox" sx={{
        width: ColumnWidths.favorite,
        px: 0, // No horizontal padding
      }}>
        <Tooltip
          title={
            <Box>
              <Typography variant="subtitle2">{contact.favorite ? 'Remove from Favorites' : 'Add to Favorites'}</Typography>
            </Box>
          }
          placement="top"
          arrow
        >
          <IconButton
            edge="end"
            aria-label="favorite"
            onClick={handleFavoriteClick}
            disabled={contact.deleting}
            size="small"
            sx={{
              color: contact.favorite ? 'gold' : alpha(theme.palette.text.primary, 0.5),
              '&:hover': {
                color: 'gold',
              },
              p: 0.25, // Smaller padding for the icon
            }}
          >
            <Star fontSize="small" sx={{
              fill: contact.favorite ? 'gold' : 'none',
              stroke: contact.favorite ? 'none' : 'currentColor',
              animation: isFavoriting ? 'pulse 0.5s ease' : 'none',
              '@keyframes pulse': {
                '0%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.3)' },
                '100%': { transform: 'scale(1)' },
              },
            }} />
          </IconButton>
        </Tooltip>
      </TableCell>

      <TableCell sx={{
        width: ColumnWidths.info,
        maxWidth: ColumnWidths.info,
        overflow: 'hidden',
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1" noWrap fontWeight={500} sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={getStatusTooltip(getContactStatus())} placement="top-start">
              <Box component="span">
                <StatusDot status={getContactStatus()} />
              </Box>
            </Tooltip>
            <Tooltip title={contactName}>
              <span>{contactName}</span>
            </Tooltip>
          </Typography>

          {/* Contact info row with email, phone and tags */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 0.75,
              width: '100%',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                height: '3px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: theme => alpha(theme.palette.divider, 0.3),
                borderRadius: '3px',
              },
            }}
          >
            {/* Email */}
            {contact.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 'fit-content' }}>
                <Tooltip title={contact.email}>
                  <Typography variant="body2" color="text.secondary" noWrap display="flex" alignItems="center">
                    <MailOutline fontSize="small" sx={{ mr: 0.5, opacity: 0.7, fontSize: '1rem' }} />
                    {contact.email}
                  </Typography>
                </Tooltip>
                <ContactEmailButton contactId={contact.id} size="small" />
              </Box>
            )}

            {/* Phone */}
            {contact.phone_number && (
              <Tooltip title={contact.phone_number}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  display="flex"
                  alignItems="center"
                  ml={contact.email ? 2 : 0}
                  sx={{ minWidth: 'fit-content' }}
                >
                  <Phone fontSize="small" sx={{ mr: 0.5, opacity: 0.7, fontSize: '1rem' }} />
                  {contact.phone_number}
                </Typography>
              </Tooltip>
            )}

            {/* Tags */}
            {contact.tags?.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  ml: (contact.phone_number || contact.email) ? 2 : 0,
                  minWidth: 'fit-content',
                }}
              >
                {contact.tags.map((tag, index) => (
                  <Tooltip key={index} title={tag}>
                    <Chip
                      label={tag}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        opacity: 0.8,
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </TableCell>

      <TableCell align="center" sx={{ width: ColumnWidths.date }}>
        <Tooltip title={formatDate(contact.created_at).full} arrow placement="top">
          <Typography variant="body2">
            {formatDate(contact.created_at).display}
          </Typography>
        </Tooltip>
      </TableCell>

      <TableCell align="center" sx={{ width: ColumnWidths.date }}>
        <Tooltip title={formatDate(contact.updated_at).full} arrow placement="top">
          <Typography variant="body2">
            {formatDate(contact.updated_at).display}
          </Typography>
        </Tooltip>
      </TableCell>

      <TableCell align="center" sx={{ width: ColumnWidths.conversations }}>
        {contact.contacted === true || (contact.contacted === undefined && contact.has_conversations) ? (
          <Chip
            label="Yes"
            size="small"
            color="success"
            variant="outlined"
            sx={{ minWidth: 55, fontSize: '0.75rem', fontWeight: 500 }}
          />
        ) : contact.contacted === false || (contact.contacted === undefined && !contact.has_conversations) ? (
          <Chip
            label="No"
            size="small"
            color="default"
            variant="outlined"
            sx={{ minWidth: 55, fontSize: '0.75rem', fontWeight: 500, opacity: 0.6 }}
          />
        ) : (
          <Chip
            label="Unknown"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ minWidth: 55, fontSize: '0.75rem', fontWeight: 500, opacity: 0.8 }}
          />
        )}
      </TableCell>

      {/* add score column */}
      <TableCell align="center" sx={{ width: ColumnWidths.score }}>
        {contact.score}
      </TableCell>

      <TableCell align="right" sx={{ width: ColumnWidths.actions, pr: 3, textAlign: 'right' }}>
        <ActionButtons>
          {isConfirming ? (
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={contact.deleting}
              size="small"
              sx={{
                fontSize: '0.75rem',
                py: 0.5,
                minWidth: 'auto',
                position: 'absolute',
                right: 0,
                animation: 'slideIn 0.2s forwards',
                '@keyframes slideIn': {
                  '0%': { opacity: 0, transform: 'translateX(20px)' },
                  '100%': { opacity: 1, transform: 'translateX(0)' }
                }
              }}
            >
              Confirm Delete
            </Button>
          ) : (
            <>
              <Tooltip
                title={
                  <Box>
                    <Typography variant="subtitle2">Edit Contact</Typography>
                  </Box>
                }
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  onClick={handleEditClick}
                  sx={{
                    color: alpha(theme.palette.primary.main, 0.6),
                    '&:hover': {
                      color: theme.palette.primary.main,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1)
                    }
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip
                title={
                  <Box>
                    <Typography variant="subtitle2">Generate AI Activity Summary</Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                      Creates a detailed activity note based on contact history
                    </Typography>
                  </Box>
                }
                placement="top"
                arrow
              >
                <Box
                  sx={{ display: 'inline-block' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <GenerateActivityButton
                    contactId={contact.id}
                    onSuccess={() => console.log(`Activity note generated for ${contact.name}`)}
                    variant="text"
                    fullWidth={false}
                    size="small"
                    label=""
                    sx={{
                      minWidth: 'auto',
                      p: '4px',
                      m: 0,
                      height: 'auto',
                      color: alpha(theme.palette.info.main, 0.6),
                      '&:hover': {
                        color: theme.palette.info.main,
                        backgroundColor: alpha(theme.palette.info.main, 0.1)
                      }
                    }}
                  />
                </Box>
              </Tooltip>

              <Tooltip
                title={
                  <Box>
                    <Typography variant="subtitle2">New Conversation</Typography>
                  </Box>
                }
                placement="top"
                arrow
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    handleOpenConversation(e);
                  }}
                  sx={{
                    color: alpha(theme.palette.success.main, 0.6),
                    '&:hover': {
                      color: theme.palette.success.main,
                      backgroundColor: alpha(theme.palette.success.main, 0.1)
                    }
                  }}
                >
                  <ChatBubbleOutline fontSize="small" />
                </IconButton>
              </Tooltip>

              <AnimatedDeleteAction>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="subtitle2">Delete Contact</Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={handleDeleteClick}
                    sx={{
                      color: alpha(theme.palette.error.main, 0.6),
                      '&:hover': {
                        color: theme.palette.error.main,
                        backgroundColor: alpha(theme.palette.error.main, 0.1)
                      }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </AnimatedDeleteAction>
            </>
          )}
        </ActionButtons>
      </TableCell>

      {/* New Conversation Dialog */}
      {conversationDialogOpen && <ConversationCreateDialogV2
        open={conversationDialogOpen}
        handleClose={() => setConversationDialogOpen(false)}
        incomingContactId={contact.id}
      />}
    </StyledTableRow>
  );
};

// Memoize to prevent unnecessary re-renders
const MemoizedContactListItem = React.memo(ContactListItem, (prevProps, nextProps) => {
  return (
    prevProps.contact.id === nextProps.contact.id &&
    prevProps.contact.updated_at === nextProps.contact.updated_at &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.handleToggle === nextProps.handleToggle &&
    prevProps.onOpenContact === nextProps.onOpenContact
  );
});

const ContactsList: React.FC<ContactsListProps> = ({ onSelectionChange, selectedContactIds, onOpenContactDialog }) => {
  const dispatch = useAppDispatch();
  const contacts = useAppSelector(selectAllFilteredContacts);
  const isFilterActive = useAppSelector(selectIsFilterActive);
  const contactsLoading = useAppSelector(state => state.filteredContacts.contactsLoading);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedContactIds);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Apply client-side sorting to contacts
  const sortedContacts = useMemo(() => {
    // Create a copy of contacts to avoid mutating the original array
    return [...contacts].sort((a, b) => {
      // Sort by name
      if (sortField === 'name') {
        const nameA = a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.phone_number || a.email || '';
        const nameB = b.name || `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.phone_number || b.email || '';
        return sortDirection === 'asc'
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      // Sort by created_at date
      if (sortField === 'created_at') {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // Sort by updated_at date
      if (sortField === 'updated_at') {
        const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      }

      // Sort by conversations status
      if (sortField === 'has_conversations') {
        const valueA = a.contacted !== undefined ? (a.contacted ? 1 : 0) : (a.has_conversations ? 1 : 0);
        const valueB = b.contacted !== undefined ? (b.contacted ? 1 : 0) : (b.has_conversations ? 1 : 0);
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }

      // Sort by score
      if (sortField === 'score') {
        const scoreA = a.score || 0;
        const scoreB = b.score || 0;
        return sortDirection === 'asc' ? scoreA - scoreB : scoreB - scoreA;
      }

      // Default sorting (no changes)
      return 0;
    });
  }, [contacts, sortField, sortDirection]);

  const handleToggle = (contactId: string) => {
    const id = contactId?.toString() || '';
    const currentIndex = selectedIds.indexOf(id);
    let newSelectedIds = [];

    if (currentIndex === -1) {
      newSelectedIds = [...selectedIds, id];
    } else {
      newSelectedIds = selectedIds.filter((sid) => sid !== id);
    }

    setSelectedIds(newSelectedIds);
    onSelectionChange(newSelectedIds); // Pass selection up
  };

  // Handler for 'Select All' checkbox change event
  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    let newSelectedIds: string[] = [];
    if (isChecked) {
      // Select all contacts
      newSelectedIds = sortedContacts.map(contact => contact.id?.toString() || '').filter(Boolean);
    } else {
      // Deselect all contacts
      newSelectedIds = [];
    }
    setSelectedIds(newSelectedIds);
    onSelectionChange(newSelectedIds); // Pass selection up
  };

  const handleOpenBulkMenu = (event: React.MouseEvent<HTMLElement>) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  const handleCloseBulkMenu = () => {
    setActionMenuAnchorEl(null);
  };

  const handleDeleteSelected = () => {
    handleCloseBulkMenu();
    if (window.confirm(`Are you sure you want to delete ${selectedIds.length} selected contacts?`)) {
      Promise.all(selectedIds.map((contactId) => dispatch(deleteContact(contactId))))
        .then(() => {
          setSelectedIds([]);
          onSelectionChange([]); // Reset selection

          if (isFilterActive) {
            dispatch(fetchFilteredContacts({}));
          }
        });
    }
  };

  const handleBulkEmail = () => {
    handleCloseBulkMenu();
    // Implement bulk email
    console.log('Bulk email for', selectedIds);
  };

  const handleBulkTag = () => {
    handleCloseBulkMenu();
    // Implement bulk tagging
    console.log('Bulk tag for', selectedIds);
  };

  useEffect(() => {
    // Update selectedIds when selectedContactIds prop changes
    setSelectedIds(selectedContactIds);
  }, [selectedContactIds]);

  // Sorting handler
  const handleSortRequest = (field: SortField) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    const newDirection = isAsc ? 'desc' : 'asc';
    setSortDirection(newDirection);
    setSortField(field);
  };

  // Add the handleOpenContact function if it doesn't exist
  const handleOpenContact = (contactId: string) => {
    if (!contactId) {
      console.error('Cannot open contact: Invalid contactId');
      return;
    }
    console.log('HERE ===========', contactId);

    if (onOpenContactDialog) {
      onOpenContactDialog(contactId);
    } else {
      setSelectedContactId(contactId);
      setEditDialogOpen(true);
    }
  };

  // Lazy-load the heavy ContactDrawer so it mounts after the click handler returns
  const ContactDrawerLazy = React.lazy(() => import('./ContactDrawer').then(module => ({ default: module.ContactDrawer })));

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <TableWrapper>
        {/* Table */}
        <Box sx={{
          overflowX: 'auto',
          maxWidth: '100%',
          flex: 1,
          overflow: 'auto',
        }}>
          <StyledTable stickyHeader aria-label="contacts table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: ColumnWidths.checkbox, pl: 1, pr: 0 }} align="center">
                  <Checkbox
                    color="primary"
                    indeterminate={selectedIds.length > 0 && selectedIds.length < sortedContacts.length}
                    checked={sortedContacts.length > 0 && selectedIds.length === sortedContacts.length}
                    onChange={handleSelectAllChange}
                  />
                </TableCell>
                <TableCell padding="checkbox" sx={{ width: ColumnWidths.favorite }} />
                <TableCell sx={{ width: ColumnWidths.info }}>
                  <TableSortLabel
                    active={sortField === 'name'}
                    direction={sortField === 'name' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('name')}
                  >
                    Contact Info
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ width: ColumnWidths.date }}>
                  <TableSortLabel
                    active={sortField === 'created_at'}
                    direction={sortField === 'created_at' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('created_at')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ width: ColumnWidths.date }}>
                  <TableSortLabel
                    active={sortField === 'updated_at'}
                    direction={sortField === 'updated_at' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('updated_at')}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ width: ColumnWidths.conversations }}>
                  <TableSortLabel
                    active={sortField === 'has_conversations'}
                    direction={sortField === 'has_conversations' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('has_conversations')}
                  >
                    Contacted
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ width: ColumnWidths.score }}>
                  <TableSortLabel
                    active={sortField === 'score'}
                    direction={sortField === 'score' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('score')}
                  >
                    Score
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: ColumnWidths.actions }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedContacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ fontSize: 48, opacity: 0.3 }} />
                      <Typography variant="h6" sx={{ opacity: 0.8 }}>
                        No contacts found
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try adjusting your search terms
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                sortedContacts.map((contact) => (
                  <MemoizedContactListItem
                    key={contact.id}
                    contact={contact}
                    isSelected={selectedIds.includes(contact.id)}
                    handleToggle={handleToggle}
                    setEditDialogOpen={setEditDialogOpen}
                    setSelectedContactId={setSelectedContactId}
                    onOpenContact={() => handleOpenContact(contact.id)}
                  />
                ))
              )}
            </TableBody>
          </StyledTable>
        </Box>
      </TableWrapper>

      {/* Only render ContactDrawer if onOpenContactDialog is not provided */}
      {!onOpenContactDialog && selectedContactId && editDialogOpen && (
        <>
          <Suspense fallback={<CircularProgress />}>
            <ContactDrawerLazy
              open={true}
              onClose={() => {
                setEditDialogOpen(false);
                setSelectedContactId(null);
              }}
              contactId={selectedContactId}
            />
          </Suspense>
        </>
      )}

      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={handleCloseBulkMenu}
      >
        <MenuItem onClick={handleBulkEmail}>
          <MailOutline fontSize="small" sx={{ mr: 1.5 }} /> Send Email
        </MenuItem>
        <MenuItem onClick={handleBulkTag}>
          <Label fontSize="small" sx={{ mr: 1.5 }} /> Add Tags
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteSelected} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1.5 }} /> Delete Selected
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ContactsList;
